import { useState } from 'react';
import type { FormEvent } from 'react';
import { Modal } from '../../components/ui/Modal';
import { Field } from '../../components/ui/Field';
import { useToast } from '../../components/ui/Toast';
import { userMutations } from '../../lib/queries';
import { useFormErrors } from '../../lib/useFormErrors';
import { ROLE_LABELS, ROLE_HINTS } from '../../lib/labels';
import type { Role, SessionUser } from '../../lib/types';

export function UserDialog({ user, onClose }: { user: SessionUser | null; onClose: () => void }) {
  const isEdit = user !== null;
  const toast = useToast();
  const { errors, capture, clear, fieldError } = useFormErrors();

  const [username, setUsername] = useState(user?.username ?? '');
  const [displayName, setDisplayName] = useState(user?.displayName ?? '');
  const [role, setRole] = useState<Role>(user?.role ?? 'planer');
  const [password, setPassword] = useState('');
  const [active, setActive] = useState(user?.active ?? true);

  const create = userMutations.useCreate();
  const update = userMutations.useUpdate();
  const busy = create.isPending || update.isPending;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    clear();

    const payload = { username: username.trim(), displayName: displayName.trim(), role, active, password };
    const onSuccess = () => {
      toast.success(isEdit ? 'Zugang gespeichert.' : `Zugang "${payload.username}" angelegt.`);
      onClose();
    };

    if (isEdit) {
      update.mutate({ ...payload, id: user.id }, { onSuccess, onError: capture });
    } else {
      create.mutate(payload, { onSuccess, onError: capture });
    }
  };

  return (
    <Modal
      title={isEdit ? 'Zugang bearbeiten' : 'Neuer Zugang'}
      subtitle={isEdit ? user.displayName : 'Anmeldung für die Arbeit im Büro'}
      onClose={onClose}
      width="voll"
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Abbrechen
          </button>
          <button type="submit" form="user-form" className="btn btn-signal" disabled={busy}>
            {busy ? 'Wird gespeichert …' : 'Speichern'}
          </button>
        </>
      }
    >
      <form id="user-form" className="stack" onSubmit={submit} noValidate>
        {errors.general ? (
          <p className="form-error" role="alert">
            {errors.general}
          </p>
        ) : null}

        <div className="form-grid">
          <span className="label form-section">Person</span>

          <Field
            label="Benutzername"
            error={fieldError('username')}
            hint="Ohne Leerzeichen und Umlaute."
            className="span-2"
          >
            {(props) => (
              <input
                {...props}
                className="input"
                autoFocus
                autoComplete="off"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
              />
            )}
          </Field>

          <Field label="Anzeigename" error={fieldError('displayName')} className="span-2">
            {(props) => (
              <input
                {...props}
                className="input"
                placeholder="Vor- und Nachname"
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
              />
            )}
          </Field>

          <span className="label form-section">Anmeldung</span>

          <Field label="Rolle" error={fieldError('role')} hint={ROLE_HINTS[role]} className="span-2">
            {(props) => (
              <select
                {...props}
                className="select"
                value={role}
                onChange={(event) => setRole(event.target.value as Role)}
              >
                {Object.entries(ROLE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <Field
            label={isEdit ? 'Neues Passwort' : 'Passwort'}
            error={fieldError('password')}
            hint={isEdit ? 'Leer lassen, um das bestehende Passwort zu behalten.' : 'Mindestens 8 Zeichen.'}
            className="span-2"
          >
            {(props) => (
              <input
                {...props}
                className="input"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            )}
          </Field>

          <span className="label form-section">Status</span>

          <label className="check span-2">
            <input type="checkbox" checked={active} onChange={(event) => setActive(event.target.checked)} />
            Aktiv — darf sich anmelden
          </label>
        </div>
      </form>
    </Modal>
  );
}
